import { useState, useEffect } from 'react';
import { txApi } from '../txApi.js';
import { C, inp, btn, th, td, Err, Spinner, Badge, SectionLabel } from './_shared.jsx';
import PlatformDisableDialog from './_PlatformDisableDialog.jsx';

/**
 * Shown before a level is deleted. A level is referenced by NAME — tx_client_platform_taxonomy
 * stores it as text and nothing has a foreign key to tx_platform_levels.id — so deleting one
 * removes a label, not the configuration under it. The taxonomies that named it and the
 * campaign rows built on them stay in the database and stop being reachable, which reads to
 * everyone else as a campaign that lost its data.
 *
 * Deleting is allowed either way; the dialog exists so it is an informed choice, and it names
 * the campaigns rather than counting them, because "3 campaigns" is not something an admin can
 * weigh. Confirm stays disabled until the usage has actually loaded — a dialog that can be
 * dismissed before it knows the cost is worse than no dialog.
 */
function LevelDeleteDialog({ level, onCancel, onConfirm }) {
  const [usage, setUsage] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    txApi.platformLevelUsage(level.id)
      .then(d => { if (alive) setUsage(d); })
      .catch(e => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [level.id]);

  const t = usage?.totals;
  const inUse = !!t && t.taxonomies > 0;

  return (
    <div className="o-scrim" onClick={onCancel}>
      <div className="o-modal" onClick={e => e.stopPropagation()}>
        <div className="o-modal-head">
          <h2>Delete {level.name}?</h2>
        </div>
        <div className="o-modal-body">
          {!usage && !err && <Spinner />}
          {err && <Err msg={err} />}

          {usage && !inUse && (
            <p style={{ margin: 0, fontSize: 13, color: C.dim }}>
              No taxonomy is defined at this level and no campaign uses it. Deleting it affects
              nothing else.
            </p>
          )}

          {usage && inUse && (
            <>
              <p style={{ margin: 0, fontSize: 13, color: C.danger, fontWeight: 600 }}>
                {t.campaigns > 0
                  ? `${t.campaigns} campaign${t.campaigns === 1 ? '' : 's'} built at this level will stop showing the ${t.rows} row${t.rows === 1 ? '' : 's'} saved against it.`
                  : 'The taxonomy defined at this level will stop being reachable.'}
              </p>

              <section>
                <SectionLabel style={{ marginBottom: 6 }}>
                  Taxonomy defined at this level
                </SectionLabel>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: C.muted }}>
                      <th style={{ padding: '6px 8px' }}>Client</th>
                      <th style={{ padding: '6px 8px' }}>Version</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Fields</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.taxonomies.map((tx, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                        <td style={{ padding: '6px 8px' }}>{tx.client_name}</td>
                        <td style={{ padding: '6px 8px', color: C.muted }}>
                          {tx.version_num ? `v${tx.version_num}` : '—'}
                          {tx.version_status === 'archived' ? ' · archived' : ''}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: C.mono }}>
                          {tx.field_count}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: C.mono,
                                     color: tx.row_count ? C.text : C.muted }}>
                          {tx.row_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              {usage.campaigns.length > 0 && (
                <section>
                  <SectionLabel style={{ marginBottom: 6 }}>
                    Campaigns built at this level
                  </SectionLabel>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.dim }}>
                    {usage.campaigns.map((c, i) => (
                      <li key={i} style={{ marginBottom: 2 }}>
                        <b style={{ color: C.text }}>{c.campaign_name}</b>
                        <span style={{ color: C.muted }}> · {c.client_name} · </span>
                        <span style={{ fontFamily: C.mono }}>{c.row_count}</span>
                        <span style={{ color: C.muted }}> row{c.row_count === 1 ? '' : 's'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
                Nothing above is deleted — a taxonomy names its level as text, so this removes
                the label and leaves the rest unreachable. Adding a level named{' '}
                <b style={{ color: C.text }}>{level.name}</b> to this platform again brings it
                all back.
              </p>
            </>
          )}
        </div>
        <div className="o-modal-foot">
          <button className="o-btn" onClick={onCancel}>Cancel</button>
          <button
            className={inUse ? 'o-btn o-btn-danger' : 'o-btn o-btn-primary'}
            disabled={!usage || busy}
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}
          >
            {busy ? 'Deleting…'
              : !inUse ? 'Delete level'
              : t.campaigns > 0 ? `Delete anyway — ${t.campaigns} campaign${t.campaigns === 1 ? '' : 's'} affected`
              : 'Delete anyway'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Up/down reordering, the same control the taxonomy field list uses.
 *
 * Order is a stored `sort_order`, not a rendering preference: the channel list and a
 * platform's levels are read top to bottom everywhere they appear — the channel picker, the
 * builder's level tabs, the duplicate preview — so where an admin puts them has to persist.
 */
function ReorderButtons({ isFirst, isLast, onUp, onDown, label }) {
  return (
    <span className="o-reorder">
      <button onClick={onUp} disabled={isFirst} aria-label={`Move ${label} up`}
        style={{ opacity: isFirst ? 0.35 : 1, cursor: isFirst ? 'default' : 'pointer' }}>▲</button>
      <button onClick={onDown} disabled={isLast} aria-label={`Move ${label} down`}
        style={{ opacity: isLast ? 0.35 : 1, cursor: isLast ? 'default' : 'pointer' }}>▼</button>
    </span>
  );
}

/**
 * Move one item one place and renumber the list 1..n, patching only what actually changed.
 *
 * Swapping just the two neighbours' stored numbers is enough only while those numbers are
 * already sequential, and they are not guaranteed to be: deletions leave gaps, and platforms
 * predating `sort_order` can share a backfilled value. Renumbering from the rendered order
 * makes the result independent of what was stored, and skipping unchanged rows keeps a move
 * to two requests in the ordinary case.
 *
 * Returns the reordered list so the caller can show it immediately.
 */
async function moveByOne(items, idx, dir, patch) {
  const other = idx + dir;
  if (other < 0 || other >= items.length) return items;
  const next = [...items];
  [next[idx], next[other]] = [next[other], next[idx]];
  await Promise.all(
    next
      .map((it, i) => [it, i + 1])
      .filter(([it, order]) => it.sort_order !== order)
      .map(([it, order]) => patch(it.id, order))
  );
  return next;
}

function LevelsPanel({ platform }) {
  const [levels,   setLevels]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [name,     setName]     = useState('');
  const [saving,   setSaving]   = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [editName, setEditName] = useState('');

  const load = () => txApi.getPlatformLevels(platform.id)
    .then(d => { setLevels(d.levels || []); setLoading(false); })
    .catch(() => setLoading(false));

  useEffect(() => { load(); }, [platform.id]);

  // A rejected name — a duplicate at this platform — has to say so. Swallowing it left the
  // level list unchanged with no message, which reads as the button being broken.
  const [nameErr, setNameErr] = useState('');

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true); setNameErr('');
    try { await txApi.createPlatformLevel(platform.id, { name: name.trim() }); setName(''); load(); }
    catch (e) { setNameErr(e.message || 'Could not add the level'); }
    finally { setSaving(false); }
  };

  const save = async (lid) => {
    setSaving(true); setNameErr('');
    try { await txApi.updatePlatformLevel(lid, { name: editName.trim() }); setEditId(null); load(); }
    catch (e) { setNameErr(e.message || 'Could not rename the level'); }
    finally { setSaving(false); }
  };

  const [orderErr, setOrderErr] = useState('');
  const move = async (idx, dir) => {
    setOrderErr('');
    const prev = levels;
    try {
      const next = await moveByOne(levels, idx, dir,
        (id, sort_order) => txApi.updatePlatformLevel(id, { sort_order }));
      setLevels(next.map((lv, i) => ({ ...lv, sort_order: i + 1 })));
    } catch (e) {
      // Put the list back rather than leaving it showing an order the database rejected.
      setLevels(prev);
      setOrderErr(e.message || 'Could not reorder');
    }
  };

  const [removeErr, setRemoveErr] = useState('');
  const [pendingRemoval, setPendingRemoval] = useState(null); // the level awaiting confirmation

  const confirmRemoval = async () => {
    setRemoveErr('');
    try { await txApi.deletePlatformLevel(pendingRemoval.id); setPendingRemoval(null); load(); }
    catch (e) { setPendingRemoval(null); setRemoveErr(e.message || 'Delete failed'); }
  };

  if (loading) return <div style={{ color: C.muted, fontSize: 12, padding: '6px 0' }}>Loading…</div>;

  return (
    <div style={{ padding: '10px 14px', background: C.bg, borderTop: `1px solid ${C.border}` }}>
      <SectionLabel style={{ marginBottom: 8 }}>
        Levels{levels.length > 1 ? ' — in order, broadest first' : ''}
      </SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {levels.map((lv, i) => (
          <div key={lv.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {editId === lv.id ? (
              <>
                <input style={inp({ width: 100 })} value={editName} autoFocus
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') save(lv.id); if (e.key === 'Escape') setEditId(null); }} />
                <button style={btn('primary', { padding: '3px 10px', fontSize: 11 })} onClick={() => save(lv.id)} disabled={saving}>✓</button>
                <button style={btn('ghost', { padding: '3px 8px', fontSize: 11 })} onClick={() => setEditId(null)}>✕</button>
              </>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 8,
                background: C.s2, border: `1px solid ${C.border}`,
              }}>
                {levels.length > 1 && (
                  <ReorderButtons label={lv.name} isFirst={i === 0} isLast={i === levels.length - 1}
                    onUp={() => move(i, -1)} onDown={() => move(i, 1)} />
                )}
                <span style={{ fontSize: 12, color: C.text }}>{lv.name}</span>
                <button onClick={() => { setEditId(lv.id); setEditName(lv.name); }}
                  style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 11, padding: '0 2px' }}>✎</button>
                <button onClick={() => setPendingRemoval(lv)} aria-label={`Delete ${lv.name}`}
                  style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 1px' }}>×</button>
              </div>
            )}
          </div>
        ))}

        {/* Add new level inline */}
        <div style={{ display: 'flex', gap: 4 }}>
          <input style={inp({ width: 110 })} placeholder="New level…" value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && create()} />
          <button style={btn('primary', { padding: '4px 12px', fontSize: 12 })} onClick={create} disabled={saving || !name.trim()}>Add</button>
        </div>
      </div>
      {(removeErr || orderErr || nameErr) && (
        <div style={{ fontSize: 11, color: C.danger, marginTop: 4, marginBottom: 4 }}>
          {removeErr || orderErr || nameErr}
        </div>
      )}
      {levels.length === 0 && (
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>
          No levels yet. Common examples: Campaign, Ad Set, Ad (Social) · Campaign, Ad Group (Search) · Campaign, Line Item, Creative (Programmatic)
        </div>
      )}
      {pendingRemoval && (
        <LevelDeleteDialog
          level={pendingRemoval}
          onCancel={() => setPendingRemoval(null)}
          onConfirm={confirmRemoval}
        />
      )}
    </div>
  );
}

export default function PlatformsPage() {
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [err,       setErr]       = useState(null);
  const [adding,    setAdding]    = useState(false);
  const [name,      setName]      = useState('');
  const [saving,    setSaving]    = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [editName,  setEditName]  = useState('');
  const [expandId,  setExpandId]  = useState(null);
  const [pendingDisable, setPendingDisable] = useState(null); // platform awaiting confirmation

  const load = () => txApi.listPlatforms()
    .then(d => { setRows(d.platforms); setLoading(false); })
    .catch(e => { setErr(e.message); setLoading(false); });

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true); setErr(null);
    try { await txApi.createPlatform({ name: name.trim() }); setName(''); setAdding(false); load(); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const save = async (id) => {
    setSaving(true);
    try { await txApi.updatePlatform(id, { name: editName.trim() }); setEditId(null); load(); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const move = async (idx, dir) => {
    setErr(null);
    const prev = rows;
    try {
      const next = await moveByOne(rows, idx, dir,
        (id, sort_order) => txApi.updatePlatform(id, { sort_order }));
      setRows(next.map((p, i) => ({ ...p, sort_order: i + 1 })));
    } catch (e) { setRows(prev); setErr(e.message); }
  };

  // Reactivating never warns — it only restores capability. Deactivating states its cost first.
  const toggle = async (r) => {
    if (r.is_active) { setPendingDisable(r); return; }
    try { await txApi.updatePlatform(r.id, { is_active: true }); load(); }
    catch (e) { setErr(e.message); }
  };

  const confirmDisable = async () => {
    setErr(null);
    try { await txApi.updatePlatform(pendingDisable.id, { is_active: false }); setPendingDisable(null); load(); }
    catch (e) { setPendingDisable(null); setErr(e.message); }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <SectionLabel>Platforms ({rows.length})</SectionLabel>
        <button style={btn('primary', { padding: '5px 14px', fontSize: 12 })} onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ Add Platform'}
        </button>
      </div>
      <Err msg={err} />

      {adding && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input style={inp({ flex: 1 })} placeholder="e.g. Meta, Google, DV360" value={name}
            onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()} autoFocus />
          <button style={btn('primary')} onClick={create} disabled={saving}>Save</button>
          <button style={btn()} onClick={() => setAdding(false)}>Cancel</button>
        </div>
      )}

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {rows.length === 0 && (
          <div style={{ ...td, textAlign: 'center', color: C.muted, padding: '24px 0' }}>No platforms yet.</div>
        )}
        {rows.map((r, i) => (
          <div key={r.id} style={{ borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : 'none' }}>
            {/* Row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
              {/* Expand toggle */}
              <button onClick={() => setExpandId(expandId === r.id ? null : r.id)}
                style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, width: 20, flexShrink: 0 }}>
                {expandId === r.id ? '▾' : '▸'}
              </button>

              {rows.length > 1 && (
                <ReorderButtons label={r.name} isFirst={i === 0} isLast={i === rows.length - 1}
                  onUp={() => move(i, -1)} onDown={() => move(i, 1)} />
              )}

              {/* Name */}
              <div style={{ flex: 1, fontSize: 13, color: C.text }}>
                {editId === r.id ? (
                  <input style={inp({ width: 200 })} value={editName} autoFocus
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && save(r.id)} />
                ) : r.name}
              </div>

              {/* Status */}
              <Badge color={r.is_active ? C.success : undefined}>{r.is_active ? 'Active' : 'Inactive'}</Badge>

              {/* Actions */}
              {editId === r.id ? (
                <span style={{ display: 'flex', gap: 6 }}>
                  <button style={btn('primary', { padding: '4px 12px' })} onClick={() => save(r.id)} disabled={saving}>Save</button>
                  <button style={btn()} onClick={() => setEditId(null)}>Cancel</button>
                </span>
              ) : (
                <span style={{ display: 'flex', gap: 6 }}>
                  <button style={btn()} onClick={() => { setEditId(r.id); setEditName(r.name); }}>Edit</button>
                  <button style={btn(r.is_active ? 'danger' : 'success', { padding: '4px 12px', fontSize: 11 })} onClick={() => toggle(r)}>
                    {r.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </span>
              )}
            </div>

            {/* Levels panel */}
            {expandId === r.id && <LevelsPanel platform={r} />}
          </div>
        ))}
      </div>

      {pendingDisable && (
        <PlatformDisableDialog
          platforms={[pendingDisable]}
          onCancel={() => setPendingDisable(null)}
          onConfirm={confirmDisable}
        />
      )}
    </div>
  );
}
